/**
 * Crews down, days across — the middle of `scheduling-main.png`.
 *
 * Rows are grouped by division and zone with a capacity bar per group, each
 * crew shows what it is and carries, and each cell shows what is on it. A cell
 * over its hours says so on the crew's bar and on the day, because §6.5 wants
 * the over-run visible rather than prevented.
 *
 * Dropping is plain HTML drag and drop: the queue sets the work item, a cell
 * accepts it. No drag library — one file of behaviour is cheaper to keep right
 * than a dependency's idea of a droppable.
 */
import { useState } from 'react'
import { Tag } from './primitives.js'
import { formatNumber, parseDateOnly } from '../lib/format.js'
import { cn } from '../lib/cn.js'
import type { BoardCrew, BoardVisit } from '../lib/schedule-types.js'

export interface CrewGridProps {
  crews: BoardCrew[]
  visits: BoardVisit[]
  dates: string[]
  selectedVisitId?: string | null
  onVisitClick?: (visit: BoardVisit) => void
  /** A work item dropped on a crew's day, or a visit moved to one. */
  onDrop?: (target: { crewId: string; date: string }, payload: DragPayload) => void
  editable?: boolean
}

export type DragPayload =
  | { kind: 'workItem'; workItemId: string; hours: number }
  | { kind: 'visit'; visitId: string; hours: number }

const dayLabel = (iso: string) => {
  const date = parseDateOnly(iso) ?? new Date()
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    day: date.getUTCDate(),
  }
}

/** "Maintenance · North zone" — the row groups the mockup draws. */
function groupKey(crew: BoardCrew): string {
  return `${crew.division ?? 'Unassigned'} · ${crew.zoneName ?? 'all zones'}`
}

export function CrewGrid({
  crews, visits, dates, selectedVisitId, onVisitClick, onDrop, editable,
}: CrewGridProps) {
  const [hover, setHover] = useState<string | null>(null)

  const groups = new Map<string, BoardCrew[]>()
  for (const crew of crews) {
    const key = groupKey(crew)
    groups.set(key, [...(groups.get(key) ?? []), crew])
  }

  const byCell = new Map<string, BoardVisit[]>()
  for (const visit of visits) {
    const key = `${visit.crewId}|${visit.date}`
    byCell.set(key, [...(byCell.get(key) ?? []), visit])
  }

  const template = `minmax(190px, 230px) repeat(${dates.length}, minmax(112px, 1fr))`

  return (
    <div className="min-w-0 overflow-x-auto">
      <div style={{ minWidth: `${190 + dates.length * 112}px` }}>
        {/* --- day headers --- */}
        <div
          className="sticky top-0 z-10 grid border-b border-line bg-surface"
          style={{ gridTemplateColumns: template }}
        >
          <div className="px-3 py-2">
            <p className="m-0 text-cell font-semibold text-text">Crews</p>
            <p className="m-0 text-sub text-text-muted">Grouped by division · zone</p>
          </div>
          {dates.map((date) => {
            const { weekday, day } = dayLabel(date)
            const booked = crews.reduce((total, crew) =>
              total + (crew.days.find((d) => d.date === date)?.bookedHours ?? 0), 0)
            const capacity = crews.reduce((total, crew) =>
              total + (crew.days.find((d) => d.date === date)?.capacityHours ?? 0), 0)
            return (
              <div key={date} className="border-l border-line-soft px-2 py-2">
                <p className="m-0 text-cell font-semibold text-text">
                  {weekday} <span className="tnum text-text-muted">{day}</span>
                </p>
                <p className={cn('m-0 tnum text-sub',
                  booked > capacity ? 'font-semibold text-danger' : 'text-text-muted')}>
                  {capacity === 0 ? 'Off' : `${formatNumber(booked)} / ${formatNumber(capacity)} h`}
                </p>
              </div>
            )
          })}
        </div>

        {/* --- groups --- */}
        {[...groups.entries()].map(([key, groupCrews]) => {
          const booked = groupCrews.reduce((total, crew) => total + crew.weekBookedHours, 0)
          const capacity = groupCrews.reduce((total, crew) => total + crew.weekCapacityHours, 0)

          return (
            <div key={key}>
              <div
                className="grid items-center border-b border-line-soft bg-surface-group"
                style={{ gridTemplateColumns: template }}
              >
                <div className="flex items-center gap-2 px-3 py-1.5">
                  <span className="text-cell font-bold text-text">{key}</span>
                  <span className="text-sub text-text-muted">
                    {groupCrews.length} {groupCrews.length === 1 ? 'crew' : 'crews'}
                  </span>
                </div>
                <div
                  className="flex items-center justify-end gap-2 px-2"
                  style={{ gridColumn: `2 / span ${dates.length}` }}
                >
                  <span className="tnum text-sub text-text-muted">
                    {formatNumber(booked)} / {formatNumber(capacity)} h this week
                  </span>
                  <CapacityBar booked={booked} capacity={capacity} className="w-[120px]" />
                </div>
              </div>

              {groupCrews.map((crew) => (
                <div
                  key={crew.id}
                  className="grid border-b border-line-soft"
                  style={{ gridTemplateColumns: template }}
                >
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-cell font-semibold text-text">
                        {crew.name}
                      </span>
                      {crew.code && <Tag>{crew.code}</Tag>}
                    </div>
                    <p className="m-0 text-sub text-text-muted">
                      {crew.memberCount} {crew.memberCount === 1 ? 'person' : 'people'}
                      {crew.truckLabel && ` · ${crew.truckLabel}`}
                    </p>
                    {crew.capabilities.length > 0 && (
                      <p className="m-0 mt-0.5 flex flex-wrap gap-1">
                        {crew.capabilities.map((capability) => (
                          <Tag key={capability}>{capability}</Tag>
                        ))}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className={cn('tnum text-sub',
                        crew.weekBookedHours > crew.weekCapacityHours
                          ? 'font-semibold text-danger'
                          : 'text-text-muted')}>
                        {formatNumber(crew.weekBookedHours)} / {formatNumber(crew.weekCapacityHours)} h
                      </span>
                      <CapacityBar
                        booked={crew.weekBookedHours}
                        capacity={crew.weekCapacityHours}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  {crew.days.map((day) => {
                    const cell = `${crew.id}|${day.date}`
                    const cellVisits = byCell.get(cell) ?? []
                    const open = Number((day.capacityHours - day.bookedHours).toFixed(2))

                    return (
                      <div
                        key={day.date}
                        onDragOver={editable
                          ? (event) => { event.preventDefault(); setHover(cell) }
                          : undefined}
                        onDragLeave={() => setHover((current) => (current === cell ? null : current))}
                        onDrop={editable
                          ? (event) => {
                            event.preventDefault()
                            setHover(null)
                            const raw = event.dataTransfer.getData('application/json')
                            if (!raw) return
                            onDrop?.({ crewId: crew.id, date: day.date },
                              JSON.parse(raw) as DragPayload)
                          }
                          : undefined}
                        className={cn(
                          'min-h-[64px] border-l border-line-soft p-1',
                          day.capacityHours === 0 && 'bg-surface-alt',
                          day.overCapacity && 'bg-danger-bg',
                          hover === cell && 'outline outline-2 -outline-offset-2 outline-accent',
                        )}
                      >
                        {cellVisits.map((visit) => (
                          <VisitCard
                            key={visit.id}
                            visit={visit}
                            selected={visit.id === selectedVisitId}
                            editable={editable}
                            onClick={() => onVisitClick?.(visit)}
                          />
                        ))}

                        {day.overCapacity && (
                          <p className="m-0 mt-1 text-sub font-semibold text-danger">
                            ⚠ {formatNumber(day.bookedHours)} h on
                            {' '}{formatNumber(day.capacityHours)} h
                          </p>
                        )}
                        {!day.overCapacity && cellVisits.length === 0 && open > 0
                          && hover === cell && (
                          <p className="m-0 text-sub text-accent">
                            {formatNumber(open)} h open
                          </p>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          )
        })}

        {crews.length === 0 && (
          <p className="m-0 px-3 py-8 text-center text-cell text-text-muted">
            No crews in this view. Add one, or widen the filters.
          </p>
        )}
      </div>
    </div>
  )
}

function CapacityBar({
  booked, capacity, className,
}: {
  booked: number
  capacity: number
  className?: string
}) {
  const ratio = capacity > 0 ? booked / capacity : (booked > 0 ? 1 : 0)
  const over = booked > capacity

  return (
    <span
      className={cn('inline-block h-1.5 overflow-hidden rounded-full bg-progress-track', className)}
      role="img"
      aria-label={`${formatNumber(booked)} of ${formatNumber(capacity)} hours booked`}
    >
      <span
        className={cn('block h-full rounded-full', over ? 'bg-danger' : 'bg-accent')}
        style={{ width: `${Math.min(100, ratio * 100)}%` }}
      />
    </span>
  )
}

function VisitCard({
  visit, selected, editable, onClick,
}: {
  visit: BoardVisit
  selected: boolean
  editable?: boolean
  onClick: () => void
}) {
  const multiDay = visit.visitsPlanned > 1

  return (
    <button
      type="button"
      draggable={editable && visit.status !== 'completed'}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/json', JSON.stringify({
          kind: 'visit', visitId: visit.id, hours: Number(visit.hoursForDay),
        }))
        event.dataTransfer.effectAllowed = 'move'
      }}
      onClick={onClick}
      className={cn(
        'mb-1 block w-full rounded-field border px-1.5 py-1 text-left',
        selected
          ? 'border-accent bg-accent-soft'
          : 'border-line-soft bg-surface hover:border-line-strong',
        visit.status === 'skipped' && 'opacity-60',
      )}
    >
      <span className="block truncate text-sub font-semibold text-text">
        {visit.propertyName}
      </span>
      <span className="block truncate text-sub text-text-muted">
        {multiDay
          ? `Day ${visit.sequence} of ${visit.visitsPlanned} · ${formatNumber(Number(visit.hoursForDay))} h`
          : `${formatNumber(Number(visit.hoursForDay))} h`}
      </span>
      {visit.status === 'skipped' && (
        <span className="block text-sub text-warn">Skipped · {visit.statusReason}</span>
      )}
    </button>
  )
}
